export function normalizeNotificationModuleKey(item) {
  const sectionKey = String(item?.notificationSectionKey || item?.sectionKey || "").trim();
  if (sectionKey) return sectionKey;
  const itemType = String(item?.type || "").trim();
  const entityType = String(item?.entityType || "").trim();
  if (itemType === "service_event" && entityType === "service_external") return "outsourced";
  if (itemType === "service_event") return "services";
  if (itemType === "occurrence_event") return "occurrences";
  if (itemType === "inspection_event") return "inspections";
  if (itemType === "veterinary_event") return "veterinary";
  return "purchases";
}

export function getNotificationModuleMeta(item) {
  const key = normalizeNotificationModuleKey(item);
  if (key === "services") return { key, label: "Serviços", referenceLabel: "Registro", openLabel: "Abrir serviço" };
  if (key === "outsourced") return { key, label: "Serviços Terceirizados", referenceLabel: "Registro", openLabel: "Abrir serviço" };
  if (key === "occurrences") return { key, label: "Ocorrências", referenceLabel: "Ocorrência", openLabel: "Abrir ocorrência" };
  if (key === "inspections") return { key, label: "Vistorias", referenceLabel: "Vistoria", openLabel: "Abrir vistoria" };
  if (key === "veterinary") return { key, label: "Veterinário", referenceLabel: "Atendimento", openLabel: "Abrir atendimento" };
  return { key: "purchases", label: "Compras", referenceLabel: "Pedido", openLabel: "Abrir pedido" };
}

export function getNotificationEntityGroupKey(item, { includeFarm=false } = {}) {
  const moduleKey = normalizeNotificationModuleKey(item);
  const entityId = String(item?.orderId || item?.entityId || item?.stockId || item?.id || "").trim() || `notification:${String(item?.id || "")}`;
  const farmKey = includeFarm ? `:${String(item?.farmId || item?.farmName || "")}` : "";
  return `${moduleKey}${farmKey}:${entityId}`;
}

export function getNotificationEventTs(item) {
  return new Date(item?.eventAt || item?.updatedAt || item?.createdAt || 0).getTime() || 0;
}

export function buildGroupedNotifications(items, { isReadFn, includeFarm=false, enrichGroup } = {}) {
  const map = new Map();
  (Array.isArray(items) ? items : []).forEach((item) => {
    if (!item) return;
    const moduleMeta = getNotificationModuleMeta(item);
    const groupKey = getNotificationEntityGroupKey(item, { includeFarm });
    const existing = map.get(groupKey) || {
      groupKey,
      moduleKey: moduleMeta.key,
      moduleLabel: moduleMeta.label,
      referenceLabel: moduleMeta.referenceLabel,
      openLabel: moduleMeta.openLabel,
      items: [],
      eventCount: 0,
      farmName: item?.farmName || "",
      farmId: item?.farmId || "",
      costCenter: item?.costCenter || "",
      requester: item?.requester || "",
      entityLabel: String(item?.orderRecordNumber || item?.entityRecordNumber || item?.orderId || item?.entityId || "").trim(),
      entityId: String(item?.orderId || item?.entityId || item?.id || "").trim(),
      type: String(item?.type || "").trim(),
    };
    existing.items.push(item);
    existing.eventCount += 1;
    existing.farmName = existing.farmName || item?.farmName || "";
    existing.farmId = existing.farmId || item?.farmId || "";
    existing.costCenter = existing.costCenter || item?.costCenter || "";
    existing.requester = existing.requester || item?.requester || "";
    existing.entityLabel = existing.entityLabel || String(item?.orderRecordNumber || item?.entityRecordNumber || item?.orderId || item?.entityId || "").trim();
    map.set(groupKey, existing);
  });
  return Array.from(map.values()).map((group) => {
    const sorted = (group.items || []).slice().sort((a,b)=> getNotificationEventTs(b) - getNotificationEventTs(a));
    const latestItem = sorted[0] || null;
    const olderItems = sorted.slice(1);
    const unreadCount = sorted.filter((item)=> !(isReadFn ? isReadFn(item) : false)).length;
    const base = {
      ...group,
      items: sorted,
      latestItem,
      olderItems,
      latestTs: getNotificationEventTs(latestItem),
      unreadCount,
      latestStatus: latestItem?.orderStatus || latestItem?.status || "",
      title: latestItem?.title || moduleMetaTitle(moduleMetaFromKey(group.moduleKey), latestItem),
      summary: latestItem?.summary || latestItem?.body || "",
      searchBlob: [
        group.moduleLabel,
        group.referenceLabel,
        group.entityLabel,
        group.requester,
        group.costCenter,
        group.farmName,
        latestItem?.title,
        latestItem?.summary,
        latestItem?.body,
        latestItem?.orderStatus,
        latestItem?.status,
      ].filter(Boolean).join(" ").toLowerCase(),
    };
    return typeof enrichGroup === "function" ? enrichGroup(base) : base;
  }).sort((a,b)=> b.latestTs - a.latestTs);
}

function moduleMetaFromKey(key) {
  return getNotificationModuleMeta({ notificationSectionKey: key });
}

function moduleMetaTitle(meta, latestItem) {
  return latestItem?.title || meta.referenceLabel;
}

export function filterNotificationGroups(groups, filters = {}) {
  const moduleKey = String(filters?.moduleKey || "all").trim();
  const query = String(filters?.query || "").trim().toLowerCase();
  const unreadOnly = !!filters?.unreadOnly;
  const datePreset = String(filters?.datePreset || "all").trim();
  const startDate = String(filters?.startDate || "").trim();
  const endDate = String(filters?.endDate || "").trim();
  const now = new Date();
  let rangeStart = null;
  let rangeEnd = null;
  if (datePreset === "today") {
    rangeStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    rangeEnd = rangeStart + 86400000 - 1;
  } else if (datePreset === "7d") {
    rangeStart = now.getTime() - (7 * 86400000);
    rangeEnd = now.getTime() + 86400000;
  } else if (datePreset === "30d") {
    rangeStart = now.getTime() - (30 * 86400000);
    rangeEnd = now.getTime() + 86400000;
  } else if (datePreset === "custom") {
    rangeStart = startDate ? new Date(`${startDate}T00:00:00`).getTime() : null;
    rangeEnd = endDate ? new Date(`${endDate}T23:59:59`).getTime() : null;
  }
  return (Array.isArray(groups) ? groups : []).filter((group) => {
    if (moduleKey !== "all" && group.moduleKey !== moduleKey) return false;
    if (unreadOnly && !(group.unreadCount > 0)) return false;
    if (query && !String(group.searchBlob || "").includes(query)) return false;
    if (rangeStart !== null || rangeEnd !== null) {
      const matchesDate = (group.items || []).some((item) => {
        const ts = getNotificationEventTs(item);
        if (rangeStart !== null && ts < rangeStart) return false;
        if (rangeEnd !== null && ts > rangeEnd) return false;
        return true;
      });
      if (!matchesDate) return false;
    }
    return true;
  });
}

export function buildNotificationModuleOptions(groups) {
  const map = new Map();
  (Array.isArray(groups) ? groups : []).forEach((group) => {
    if (!group?.moduleKey || map.has(group.moduleKey)) return;
    map.set(group.moduleKey, group.moduleLabel || group.moduleKey);
  });
  return [{ value: "all", label: "Todos os módulos" }, ...Array.from(map.entries()).map(([value, label]) => ({ value, label }))];
}
